import React, { useState } from "react";
import { Button, Table, Card, Alert, Spinner } from "react-bootstrap";
import axios from "axios";
import { useNavigate } from "react-router-dom";
// Optional: import Papa from 'papaparse'; if CSV generation for errors is needed

const ImportStep3 = ({ uploadedFile, validationResult, onBack, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [importResult, setImportResult] = useState(null);
  const navigate = useNavigate();

  const handleConfirm = async () => {
    const readyRows = validationResult.rows.filter((r) => r.status === "ready");
    if (readyRows.length === 0) return;

    setLoading(true);
    setError("");

    try {
      const token = localStorage.getItem("jff_token");
      const res = await axios.post(
        `${process.env.REACT_APP_API_END_POINT}/exhibitors/import/confirm`,
        {
          rows: readyRows,
          filename: uploadedFile.name,
          skippedDuplicates: validationResult.summary.duplicates,
          skippedErrors: validationResult.summary.errors,
        },
        { headers: { token } }
      );

      if (res.data.status) {
        setImportResult(res.data.data);
        setTimeout(() => {
          navigate("/superadmin/exhibitor-list");
          onSuccess();
        }, 3000);
      }
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.message || "Import failed.");
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadErrorReport = () => {
    if (!importResult || !importResult.failed || importResult.failed.length === 0) return;
    
    // Simple CSV generator
    const headers = "Company Name,Mobile No.,Reason\n";
    const csvContent = importResult.failed.map(r => `"${r.companyName}","${r.mobileNo}","${r.reason}"`).join("\n");
    
    const blob = new Blob([headers + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'import_errors.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (importResult) {
    return (
      <Card className="shadow-sm">
        <Card.Body className="text-center py-5">
          <h2 className="text-success mb-3">Import Complete!</h2>
          <h4>{importResult.imported} Exhibitors Imported Successfully</h4>
          {importResult.failed?.length > 0 && (
            <div className="mt-4">
              <Alert variant="warning">
                {importResult.failed.length} rows failed during final re-validation.
              </Alert>
              <Button variant="outline-danger" onClick={handleDownloadErrorReport}>
                Download Error Report
              </Button>
            </div>
          )}
          <p className="text-muted mt-4">Redirecting to exhibitor list...</p>
        </Card.Body>
      </Card>
    );
  }

  const { summary } = validationResult;
  const readyRows = validationResult.rows.filter((r) => r.status === "ready");

  return (
    <Card className="shadow-sm">
      <Card.Body>
        <h5 className="mb-4">Step 3: Confirm & Import</h5>

        {error && <Alert variant="danger">{error}</Alert>}

        <Card className="bg-light mb-4 border-0">
          <Card.Body>
            <div className="d-flex justify-content-between mb-2">
              <span className="text-muted">Total rows in file:</span>
              <span className="fw-bold">{summary.total}</span>
            </div>
            <div className="d-flex justify-content-between mb-2">
              <span className="text-muted">Skipped (duplicates):</span>
              <span className="fw-bold text-warning">{summary.duplicates}</span>
            </div>
            <div className="d-flex justify-content-between mb-2">
              <span className="text-muted">Skipped (errors):</span>
              <span className="fw-bold text-danger">{summary.errors}</span>
            </div>
            <hr />
            <div className="d-flex justify-content-between">
              <span className="fs-5 text-primary">Will be imported:</span>
              <span className="fs-5 fw-bold text-primary">{summary.ready}</span>
            </div>
          </Card.Body>
        </Card>

        <h6 className="fw-bold mb-3">Preview of Rows to Import</h6>
        <div style={{ maxHeight: "300px", overflowY: "auto" }}>
          <Table responsive bordered hover size="sm">
            <thead className="bg-light" style={{ position: "sticky", top: 0 }}>
              <tr>
                <th>Company Name</th>
                <th>Contact Person</th>
                <th>Mobile No.</th>
                <th>Stall Size</th>
              </tr>
            </thead>
            <tbody>
              {readyRows.slice(0, 50).map((row, idx) => (
                <tr key={idx}>
                  <td>{row.companyName}</td>
                  <td>{row.contactPerson}</td>
                  <td>{row.mobileNo}</td>
                  <td>{row.stallSize}</td>
                </tr>
              ))}
            </tbody>
          </Table>
          {readyRows.length > 50 && (
            <p className="text-muted text-center mt-2">
              Showing first 50 rows...
            </p>
          )}
        </div>

        <div className="d-flex justify-content-between mt-4">
          <Button variant="secondary" onClick={onBack} disabled={loading}>
            Back
          </Button>
          <Button variant="success" onClick={handleConfirm} disabled={loading || readyRows.length === 0}>
            {loading ? <Spinner size="sm" /> : "Confirm & Import"}
          </Button>
        </div>
      </Card.Body>
    </Card>
  );
};

export default ImportStep3;
